import React, { useState, useEffect } from 'react';

interface CanadaDashboardProps {
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

const FeatureBox: React.FC<{ href: string; icon: string; text: string }> = ({ href, icon, text }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-center no-underline text-white block hover:opacity-80 hover:scale-105 transition-all duration-200"
  >
    <div className="text-5xl mb-2">{icon}</div>
    <p className="text-sm font-medium m-0">{text}</p>
  </a>
);

const CanadaDashboard: React.FC<CanadaDashboardProps> = ({
  activeSection: externalActiveSection,
  onSectionChange: externalOnSectionChange
}) => {
  const [weather, setWeather] = useState<any>(null);
  const [selectedCity, setSelectedCity] = useState('Toronto');
  const [loading, setLoading] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<any>(null);
  const [internalActiveSection, setInternalActiveSection] = useState('overview');

  const activeSection = externalActiveSection ?? internalActiveSection;
  const setActiveSection = externalOnSectionChange ?? setInternalActiveSection;

  const cities = [
    { name: 'Toronto', lat: 43.70, lon: -79.42 },
    { name: 'Vancouver', lat: 49.28, lon: -123.12 },
    { name: 'Montreal', lat: 45.50, lon: -73.57 },
    { name: 'Calgary', lat: 51.05, lon: -114.07 },
    { name: 'Ottawa', lat: 45.42, lon: -75.70 },
  ];

  useEffect(() => {
    if (activeSection === 'weather') fetchWeather();
    if (activeSection === 'currency') fetchExchangeRate();
  }, [activeSection, selectedCity]);

  const fetchWeather = async () => {
    setLoading(true);
    try {
      const city = cities.find(c => c.name === selectedCity)!;
      const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&current=temperature_2m,relative_humidity_2m,precipitation,weather_code,wind_speed_10m&timezone=America/Toronto`);
      const data = await res.json();
      setWeather(data.current);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchExchangeRate = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://api.exchangerate-api.com/v4/latest/CAD');
      const data = await res.json();
      setExchangeRate(data.rates);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const getWeatherIcon = (code: number) => {
    if (code === 0) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '❄️';
    return '🌤️';
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: 'ℹ️' },
    { id: 'weather', label: 'Weather', icon: '🌤️' },
    { id: 'population', label: 'Population', icon: '👥' },
    { id: 'economy', label: 'Economy', icon: '📈' },
    { id: 'currency', label: 'Currency', icon: '💱' },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="flex flex-col gap-6">
            <div className="bg-[#003366] text-white p-12 rounded-xl text-center">
              <h2 className="text-4xl m-0 mb-2">🍁 Welcome to Canada</h2>
              <p className="text-xl m-0 opacity-90">Essential information for newcomers</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">General Information</h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-3">
                  {[
                    ['Capital', 'Ottawa'],
                    ['Population', '~40 million'],
                    ['Area', '9.98 million km²'],
                    ['Official Languages', 'English & French'],
                    ['Currency', 'Canadian Dollar (CAD)'],
                  ].map(([k, v]) => (
                    <li key={k}><strong>{k}:</strong> {v}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Major Provinces</h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-3">
                  {['🏙️ Ontario (Toronto)', '🏔️ British Columbia (Vancouver)', '🎭 Quebec (Montreal)', '⛰️ Alberta (Calgary)', '🌊 Nova Scotia (Halifax)'].map(p => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-[#0066CC] p-8 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-4">🎯 Key Features</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-4">
                <FeatureBox href="https://www.canada.ca/en/health-canada.html" icon="🏥" text="Universal Healthcare" />
                <FeatureBox href="https://www.canada.ca/en/services/benefits/education.html" icon="🎓" text="Quality Education" />
                <FeatureBox href="https://parks.canada.ca/" icon="🌲" text="Beautiful Nature" />
                <FeatureBox href="https://www.canada.ca/en/immigration-refugees-citizenship/services/new-immigrants.html" icon="🤝" text="Multicultural Society" />
              </div>
            </div>
          </div>
        );

      case 'weather':
        return (
          <div className="flex flex-col gap-6">
            <h2 className="text-3xl font-bold text-gray-900">🌤️ Weather Information</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <label className="block font-bold text-gray-700 mb-2">Select City:</label>
              <select
                value={selectedCity}
                onChange={e => setSelectedCity(e.target.value)}
                className="w-full p-3 text-base border-2 border-gray-200 rounded-lg cursor-pointer"
              >
                {cities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="text-center py-12 text-lg text-gray-500">Loading weather data...</div>
            ) : weather ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#0066CC] text-white p-8 rounded-xl text-center">
                  <div className="text-6xl mb-4">{getWeatherIcon(weather.weather_code)}</div>
                  <div className="text-5xl font-bold mb-2">{weather.temperature_2m}°C</div>
                  <div className="text-2xl opacity-90">{selectedCity}</div>
                </div>
                <div className="flex flex-col gap-4">
                  {[
                    { label: '💧 Humidity', value: `${weather.relative_humidity_2m}%` },
                    { label: '💨 Wind Speed', value: `${weather.wind_speed_10m} km/h` },
                    { label: '🌧️ Precipitation', value: `${weather.precipitation} mm` },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-white p-6 rounded-lg shadow-sm">
                      <div className="text-sm text-gray-500 mb-2">{label}</div>
                      <div className="text-3xl font-bold text-gray-900">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        );

      case 'population':
        return (
          <div className="flex flex-col gap-6">
            <h2 className="text-3xl font-bold text-gray-900">👥 Population Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Total Population', value: '~40M', sub: 'Million people' },
                { title: 'Population Density', value: '4', sub: 'People per km²' },
              ].map(({ title, value, sub }) => (
                <div key={title} className="bg-white p-8 rounded-xl shadow-sm text-center">
                  <h3 className="text-base text-gray-500 mb-4">{title}</h3>
                  <div className="text-6xl font-bold text-[#003366] mb-2">{value}</div>
                  <p className="text-sm text-gray-500">{sub}</p>
                </div>
              ))}
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Major Cities</h3>
              {[
                { city: 'Toronto', pop: '6.2 million' },
                { city: 'Montreal', pop: '4.3 million' },
                { city: 'Vancouver', pop: '2.6 million' },
                { city: 'Calgary', pop: '1.6 million' },
                { city: 'Ottawa', pop: '1.4 million' },
              ].map(({ city, pop }) => (
                <div key={city} className="flex justify-between py-4 border-b border-gray-100 last:border-0">
                  <span className="font-medium text-gray-700">{city}</span>
                  <span className="text-[#0066CC] font-bold">{pop}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'economy':
        return (
          <div className="flex flex-col gap-6">
            <h2 className="text-3xl font-bold text-gray-900">📈 Economy Overview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { label: 'GDP', value: '$2.1T', unit: 'Trillion USD' },
                { label: 'GDP per Capita', value: '$52K', unit: 'Thousand USD' },
                { label: 'Unemployment Rate', value: '~5%', unit: 'Percentage' },
              ].map(({ label, value, unit }) => (
                <div key={label} className="bg-white p-8 rounded-xl shadow-sm text-center">
                  <h3 className="text-sm text-gray-500 mb-4">{label}</h3>
                  <div className="text-5xl font-bold text-[#003366] mb-2">{value}</div>
                  <p className="text-sm text-gray-500">{unit}</p>
                </div>
              ))}
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Key Industries</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {[
                  { icon: '🛢️', name: 'Oil & Gas', desc: 'Major producer' },
                  { icon: '🏭', name: 'Manufacturing', desc: 'Advanced production' },
                  { icon: '🌾', name: 'Agriculture', desc: 'Food exporter' },
                  { icon: '💻', name: 'Technology', desc: 'Innovation hub' },
                ].map(({ icon, name, desc }) => (
                  <div key={name} className="p-6 border-2 border-gray-100 rounded-lg text-center">
                    <div className="text-5xl mb-2">{icon}</div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">{name}</h4>
                    <p className="text-sm text-gray-500 m-0">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'currency':
        return (
          <div className="flex flex-col gap-6">
            <h2 className="text-3xl font-bold text-gray-900">💱 Canadian Dollar Exchange Rates</h2>
            {loading ? (
              <div className="text-center py-12 text-lg text-gray-500">Loading exchange rates...</div>
            ) : exchangeRate ? (
              <>
                <div className="bg-emerald-500 text-white p-6 rounded-lg text-center">
                  <h3 className="text-2xl m-0">1 CAD equals:</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { code: 'USD', name: 'US Dollar', flag: '🇺🇸' },
                    { code: 'EUR', name: 'Euro', flag: '🇪🇺' },
                    { code: 'GBP', name: 'British Pound', flag: '🇬🇧' },
                    { code: 'JPY', name: 'Japanese Yen', flag: '🇯🇵' },
                    { code: 'AUD', name: 'Australian Dollar', flag: '🇦🇺' },
                    { code: 'CHF', name: 'Swiss Franc', flag: '🇨🇭' },
                  ].map(({ code, name, flag }) => (
                    <div key={code} className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-100">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-3xl">{flag}</span>
                        <span className="text-lg font-bold text-gray-900">{code}</span>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">{name}</p>
                      <div className="text-3xl font-bold text-[#0066CC]">
                        {exchangeRate[code]?.toFixed(4)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 text-sm text-blue-800">
                  <strong>Note:</strong> Exchange rates are updated in real-time and may differ from bank rates.
                </div>
              </>
            ) : null}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Tab Bar */}
      <div className="flex gap-2 flex-wrap bg-white p-4 rounded-lg shadow-sm mb-8">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className="flex items-center gap-2 px-6 py-3 rounded-lg border-none cursor-pointer text-base font-medium transition-all"
            style={{
              backgroundColor: activeSection === section.id ? '#003366' : '#F3F4F6',
              color: activeSection === section.id ? 'white' : '#1F2937',
            }}
          >
            <span className="text-xl">{section.icon}</span>
            <span>{section.label}</span>
          </button>
        ))}
      </div>

      <div className="min-h-[400px]">
        {renderSection()}
      </div>
    </div>
  );
};

export default CanadaDashboard;
